import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Autocomplete,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  Category as CategoryIcon,
  Person as PersonIcon,
  Inventory as ProductIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { ConsultaJerarquiaTopeDescuento, JerarquiaTopeDescuento, ConsultaFlexibleRequest, ConsultaFlexibleResult, ConsultaResumenTopes } from '../../types';

const ConsultaTopes: React.FC = () => {
  const [filtros, setFiltros] = useState<ConsultaFlexibleRequest>({
    soloActivos: true,
    limiteResultados: 20
  });
  const [resultados, setResultados] = useState<ConsultaJerarquiaTopeDescuento[]>([]);

  // Consulta de resumen
  const resumenQuery = useQuery({
    queryKey: ['resumen-topes'],
    queryFn: () => apiService.obtenerResumenTopes(),
    staleTime: 300000, // 5 minutos
  });

  // Consulta flexible
  const consultaFlexibleMutation = useMutation({
    mutationFn: (request: ConsultaFlexibleRequest) =>
      apiService.consultarTopesFlexible(request),
    onSuccess: (data: ConsultaFlexibleResult) => {
      setResultados(data.resultados);
    },
    onError: (error: any) => {
      console.error('Error al consultar topes:', error);
    },
  });

  // Nueva función para habilitar el botón en consulta flexible
  const tieneFiltrosFlexibles = () => {
    return (
      (filtros.clienteId && filtros.clienteId.trim() !== '') ||
      (filtros.productoId && filtros.productoId.trim() !== '') ||
      (filtros.rubroCodigo && filtros.rubroCodigo.trim() !== '')
    );
  };

  const handleConsultaFlexible = () => {
    consultaFlexibleMutation.mutate(filtros);
  };

  const limpiarFiltros = () => {
    setFiltros({
      soloActivos: true,
      limiteResultados: 20
    });
    setResultados([]);
    consultaFlexibleMutation.reset();
  };

  // Nueva función para exportar a Excel
  const handleExportarExcel = async () => {
    try {
      const blob = await apiService.exportarTopesExcel(filtros);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Usar la hora local del usuario para el nombre del archivo
      const now = new Date();
      const localTime = now.toLocaleString('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/[/:]/g, '').replace(/,/g, '').replace(/\s/g, '');
      
      link.setAttribute('download', `TopesDescuento_${localTime}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Error al exportar a Excel.');
    }
  };

  const getPrioridadIcon = (prioridad: number) => {
    switch (prioridad) {
      case 1:
        return <ProductIcon color="success" />;
      case 2:
        return <CategoryIcon color="secondary" />;
      case 3:
        return <PersonIcon color="primary" />;
      case 0:
        return <SettingsIcon color="disabled" />;
      default:
        return <InfoIcon />;
    }
  };

  const getPrioridadColor = (prioridad: number) => {
    switch (prioridad) {
      case 1:
        return 'success';
      case 2:
        return 'secondary';
      case 3:
        return 'primary';
      case 0:
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (aplicable: boolean, activo: boolean) => {
    if (!aplicable) return <CancelIcon color="error" />;
    if (!activo) return <WarningIcon color="warning" />;
    return <CheckCircleIcon color="success" />;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Consulta de Topes de Descuento
      </Typography>

      {/* Resumen de estadísticas */}
      {/* 
      {resumenQuery.data && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Resumen del Sistema
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Topes Cliente
                </Typography>
                <Typography variant="h6" color="primary">
                  {resumenQuery.data.totalTopesCliente}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Topes Cliente-Rubro
                </Typography>
                <Typography variant="h6" color="secondary">
                  {resumenQuery.data.totalTopesClienteRubro}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Topes Cliente-Producto
                </Typography>
                <Typography variant="h6" color="success">
                  {resumenQuery.data.totalTopesClienteProducto}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Activos
                </Typography>
                <Typography variant="h6" color="success">
                  {resumenQuery.data.topesActivos}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      */}

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {'Filtros de Consulta'}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ID del Cliente"
                value={filtros.clienteId || ''}
                onChange={(e) => setFiltros({ ...filtros, clienteId: e.target.value })}
                placeholder="Ej: CLI001"
                variant="outlined"
                size="small"
                helperText="Opcional - Dejar vacío para consultar todos"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ID del Producto"
                value={filtros.productoId || ''}
                onChange={(e) => setFiltros({ ...filtros, productoId: e.target.value })}
                placeholder="Ej: PROD001"
                variant="outlined"
                size="small"
                helperText="Opcional - Dejar vacío para consultar todos"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rubro"
                value={filtros.rubroCodigo || ''}
                onChange={(e) => setFiltros({ ...filtros, rubroCodigo: e.target.value })}
                placeholder="Ej: GENERAL"
                variant="outlined"
                size="small"
                helperText="Opcional - Dejar vacío para consultar todos"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Límite de Resultados"
                type="number"
                value={filtros.limiteResultados || 20}
                onChange={(e) => setFiltros({ ...filtros, limiteResultados: parseInt(e.target.value) || 20 })}
                variant="outlined"
                size="small"
                helperText="Máximo número de resultados"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filtros.soloActivos}
                    onChange={(e) => setFiltros({ ...filtros, soloActivos: e.target.checked })}
                  />
                }
                label="Solo topes activos"
              />
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleConsultaFlexible}
              disabled={
                !tieneFiltrosFlexibles() || consultaFlexibleMutation.isPending
              }
            >
              {consultaFlexibleMutation.isPending 
                ? <CircularProgress size={20} /> 
                : 'Consultar'
              }
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={limpiarFiltros}
            >
              Limpiar
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleExportarExcel}
              sx={{ ml: 2 }}
              disabled={!tieneFiltrosFlexibles()}
            >
              Exportar a Excel
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Mensajes de error */}
      {consultaFlexibleMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error al consultar los topes de descuento. Verifica que los datos ingresados sean correctos.
        </Alert>
      )}

      {/* Resultados */}
      {resultados.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Resultados ({resultados.length})
            </Typography>

            {resultados.map((resultado, index) => (
              <Paper key={index} elevation={2} sx={{ p: 3, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Consulta {index + 1}: {resultado.clienteRazonSocial} - {resultado.productoDetalle}
                </Typography>

                {/* Información del Cliente y Producto */}
                <Grid container spacing={3} sx={{ mb: 2 }}>
                  {/* Cliente */}
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <PersonIcon color={resultado.clienteExiste ? "success" : "error"} sx={{ mr: 1 }} />
                      <Typography variant="body2" fontWeight="bold">
                        Cliente: {resultado.clienteId && resultado.clienteId.trim() !== '' ? `${resultado.clienteId.trim()} - ` : ''}{resultado.clienteRazonSocial}
                      </Typography>
                    </Box>
                    <Chip 
                      label={resultado.clienteExiste ? "Cliente encontrado" : "Cliente no encontrado"}
                      color={resultado.clienteExiste ? "success" : "error"}
                      size="small"
                    />
                  </Grid>

                  {/* Producto (solo si hay productoId) */}
                  {resultado.productoId && resultado.productoId.trim() !== '' && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <ProductIcon color={resultado.productoExiste ? "success" : "error"} sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="bold">
                          Producto: {resultado.productoId.trim()} - {resultado.productoDetalle}
                        </Typography>
                      </Box>
                      <Chip 
                        label={resultado.productoExiste ? "Producto encontrado" : "Producto no encontrado"}
                        color={resultado.productoExiste ? "success" : "error"}
                        size="small"
                      />
                    </Grid>
                  )}

                  {/* Rubro (solo si hay rubroCodigo) */}
                  {resultado.rubroCodigo && resultado.rubroCodigo.trim() !== '' && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <CategoryIcon color="warning" sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="bold">
                          Rubro: {resultado.rubroCodigo.trim()}{resultado.rubroNombre && resultado.rubroNombre.trim() !== '' ? ` - ${resultado.rubroNombre.trim()}` : ''}
                        </Typography>
                      </Box>
                      <Chip
                        label={resultado.rubroNombre && resultado.rubroNombre.trim() !== '' ? "Rubro encontrado" : "Rubro no encontrado"}
                        color={resultado.rubroNombre && resultado.rubroNombre.trim() !== '' ? "success" : "error"}
                        size="small"
                      />
                    </Grid>
                  )}
                </Grid>

                {/* Tope Aplicado */}
                {resultado.topeAplicado && (
                  <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'white' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center">
                          <TrendingUpIcon sx={{ mr: 1 }} />
                          <Typography variant="h5">
                            {resultado.topeAplicado.topeDescuento}%
                          </Typography>
                          {!resultado.topeAplicado.activo && (
                            <Chip label="Inactivo" color="error" size="small" sx={{ ml: 2, bgcolor: 'error.main', color: 'white' }} />
                          )}
                          {resultado.topeAplicado.vencido && (
                            <Chip label="Vencido" color="warning" size="small" sx={{ ml: 2, bgcolor: 'warning.main', color: 'black' }} />
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center">
                          {getPrioridadIcon(resultado.topeAplicado.prioridad)}
                          <Typography variant="body1" sx={{ ml: 1 }}>
                            {resultado.topeAplicado.tipo}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                {/* Jerarquías Detalladas */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2">
                      Ver jerarquías disponibles
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense>
                      {resultado.jerarquias.map((jerarquia, jIndex) => (
                        <ListItem key={jIndex} sx={{ border: '1px solid', borderColor: 'divider', mb: 1, borderRadius: 1 }}>
                          <ListItemIcon>
                            {getStatusIcon(jerarquia.aplicable, jerarquia.activo)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" fontWeight="bold">
                                  {jerarquia.tipo}
                                </Typography>
                                <Chip
                                  label={`${jerarquia.topeDescuento}%`}
                                  color={getPrioridadColor(jerarquia.prioridad) as any}
                                  size="small"
                                />
                                {!jerarquia.activo && (
                                  <Chip label="Inactivo" color="error" size="small" />
                                )}
                                {jerarquia.vencido && (
                                  <Chip label="Vencido" color="warning" size="small" />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                {jerarquia.mensaje}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              </Paper>
            ))}

            {/* 
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Explicación:</strong> El sistema evalúa las jerarquías en orden de prioridad. 
                Se aplica la primera configuración válida encontrada. Si no hay configuraciones específicas, 
                se usa el tope por defecto del sistema.
              </Typography>
            </Alert>
            */}
          </CardContent>
        </Card>
      )}

      {/* Mensaje cuando no hay resultados tras una consulta */}
      {(consultaFlexibleMutation.isSuccess && resultados.length === 0) && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No se encontraron resultados para los filtros ingresados.
        </Alert>
      )}
    </Box>
  );
};

export default ConsultaTopes;
